import React, { useCallback, useRef } from "react";

import { Icon, Text } from "@orgmym/leeloo-core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { Skeleton } from "../skeleton";
import * as styles from "./scrollable-tabs.css";
import { useScrollEdges } from "./use-scroll-edges";

// Fraction of the visible width to advance per arrow click.
const SCROLL_STEP_RATIO = 0.6;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export interface ScrollableTab<T extends string = string> {
  id: T;
  label: string;
  count?: number;
  isLoading?: boolean;
}

export interface ScrollableTabsProps<T extends string = string> {
  activeTab: T;
  onTabChange: (tab: T) => void;
  tabs: ScrollableTab<T>[];
  className?: string;
}

export const ScrollableTabs = <T extends string = string>({
  activeTab,
  onTabChange,
  tabs,
  className = ""
}: ScrollableTabsProps<T>) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { canScrollLeft, canScrollRight } = useScrollEdges(containerRef, [
    tabs
  ]);

  const handleTabClick = (tabId: T) => {
    onTabChange(tabId);
  };

  const scrollByDirection = useCallback((direction: "left" | "right") => {
    const el = containerRef.current;
    if (!el) return;

    const amount = el.clientWidth * SCROLL_STEP_RATIO;
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: prefersReducedMotion() ? "auto" : "smooth"
    });
  }, []);

  return (
    <div
      className={clsx(styles.scrollableTabs, className)}
      data-testid="scrollable-tabs"
    >
      {canScrollLeft && (
        <>
          <div className={styles.fadeLeft} aria-hidden="true" />
          <button
            type="button"
            className={styles.scrollButtonLeft}
            onClick={() => scrollByDirection("left")}
            aria-label={t(
              "creators_messaging_tabs_scroll_left",
              "Scroll tabs left"
            )}
            tabIndex={-1}
            data-testid="scrollable-tabs-scroll-left"
          >
            <Icon name="chevron-left" width={20} height={20} />
          </button>
        </>
      )}

      <div
        className={styles.scrollableContainer}
        ref={containerRef}
        data-testid="scrollable-tabs-container"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={clsx(
              styles.tab,
              activeTab === tab.id && styles.tabActive
            )}
            role="tab"
            aria-selected={activeTab === tab.id}
            data-testid={`scrollable-tab-${tab.id}`}
          >
            <span className={styles.tabContent}>
              <Text
                className={clsx(
                  styles.tabLabel,
                  activeTab === tab.id && styles.tabLabelActive
                )}
                variant="default"
                weight="medium"
                data-testid={`scrollable-tab-label-${tab.id}`}
              >
                {tab.label}
              </Text>
              {tab.id !== "all" && (
                <>
                  {tab.isLoading ? (
                    <Skeleton
                      width="20px"
                      height="16px"
                      className={styles.tabCountSkeleton}
                      data-testid={`scrollable-tab-count-skeleton-${tab.id}`}
                    />
                  ) : (
                    tab.count != null && (
                      <span
                        className={clsx(
                          styles.tabCount,
                          activeTab === tab.id && styles.tabCountActive
                        )}
                        data-testid={`scrollable-tab-count-${tab.id}`}
                      >
                        {tab.count >= 100 ? "99+" : tab.count}
                      </span>
                    )
                  )}
                </>
              )}
            </span>
          </button>
        ))}
      </div>

      {canScrollRight && (
        <>
          <div className={styles.fadeRight} aria-hidden="true" />
          <button
            type="button"
            className={styles.scrollButtonRight}
            onClick={() => scrollByDirection("right")}
            aria-label={t(
              "creators_messaging_tabs_scroll_right",
              "Scroll tabs right"
            )}
            tabIndex={-1}
            data-testid="scrollable-tabs-scroll-right"
          >
            <Icon name="chevron-right" width={20} height={20} />
          </button>
        </>
      )}
    </div>
  );
};

export default ScrollableTabs;
